import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Checkbox,
    FormControlLabel,
    TextField,
    Button,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Autocomplete
} from "@mui/material";
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import UsersAutoComplete from "../../Users/UsersAutoComplete";
import { DealStage } from "../../../utils/enums";
import AccountsAutoComplete from "../../Accounts/AccountsAutoComplete";
import ContactsAutoComplete from "../../Contacts/ContactsAutoComplete";
import dayjs from "dayjs";

const FilterComponent = ({ filters, setFilters }) => {
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [fromDateClosingDate, setFromDateClosingDate] = useState(null);
    const [toDateClosingDate, setToDateClosingDate] = useState(null);
    const [checkedFields, setCheckedFields] = useState([
        { fieldName: "dealName", label: "Deal Name", checked: false },
        { fieldName: "accountId", label: "Account", checked: false },
        { fieldName: "account.accountName", label: "Account Name", checked: false },
        { fieldName: "contactId", label: "Contact", checked: false },
        { fieldName: "contact.firstName", label: "Contact Name", checked: false },
        { fieldName: "amount", label: "Amount", checked: false },
        { fieldName: "stage", label: "Stage", checked: false },
        { fieldName: "closingDate", label: "Closing Date", checked: false },
        { fieldName: "ownerId", label: "Deal Owner", checked: false },
        { fieldName: "country", label: "Country", checked: false },
        { fieldName: "createdBy", label: "Created By", checked: false },
        { fieldName: "createdAt", label: "Created Time", checked: false },
        { fieldName: "updatedBy", label: "Modified By", checked: false },
    ]);

    const [filterValues, setFilterValues] = useState({});
    const [filterOperators, setFilterOperators] = useState({});

    // Initialize state when filters prop is received
    // This is needed when "storing filter state in localstorage"
    useEffect(() => {
        if (filters && filters.length > 0) {
            const updatedCheckedFields = checkedFields.map((field) => {
                const matchingFilter = filters.find(
                    (filter) => filter.fieldName === field.fieldName
                );
                if (matchingFilter) {
                    return { ...field, checked: true };
                }
                return field;
            });

            setCheckedFields(updatedCheckedFields);

            const initialFilterValues = {};
            const initialFilterOperators = {};

            filters.forEach((filter) => {
                initialFilterValues[filter.fieldName] = filter.fieldValue;
                initialFilterOperators[filter.fieldName] = filter.filterOperator;
            });

            setFilterValues(initialFilterValues);
            setFilterOperators(initialFilterOperators);

            // to handle date
            const dateFilter = filters.find(f => f.fieldName === 'createdAt');
            if (dateFilter?.fieldValue) {
                const [fromDateStr, toDateStr] = dateFilter.fieldValue.split(';');
                setFromDate(fromDateStr ? dayjs(fromDateStr) : null);
                setToDate(toDateStr ? dayjs(toDateStr) : null);
            }

            const closingDateFilter = filters.find(f => f.fieldName === 'closingDate');
            if (closingDateFilter?.fieldValue) {

                const [fromDateStrClosingDate, toDateStrClosingDate] = closingDateFilter.fieldValue.split(';');
                setFromDateClosingDate(fromDateStrClosingDate ? dayjs(fromDateStrClosingDate) : null);
                setToDateClosingDate(toDateStrClosingDate ? dayjs(toDateStrClosingDate) : null);
            }
        }
    }, [filters]);

    const handleCheckboxChange = (fieldName) => {
        setCheckedFields((prev) =>
            prev.map((field) =>
                field.fieldName === fieldName
                    ? { ...field, checked: !field.checked }
                    : field
            )
        );

        if (!filterValues[fieldName]) {
            setFilterValues({ ...filterValues, [fieldName]: "" });
            if (fieldName === "stage" || fieldName === "amount" || fieldName === "accountId" || fieldName === "contactId") {
                setFilterOperators({ ...filterOperators, [fieldName]: 0 }); // operator "Equal" for enums
            }
            else if (["closingDate", "createdAt"].includes(fieldName)) {
                setFilterOperators({ ...filterOperators, [fieldName]: 14 }); // operator "between" for dates
            }
            else {
                setFilterOperators({ ...filterOperators, [fieldName]: 2 }); // Default operator (e.g., "contains")
            }
        } else {
            const updatedValues = { ...filterValues };
            const updatedOperators = { ...filterOperators };
            delete updatedValues[fieldName];
            delete updatedOperators[fieldName];
            setFilterValues(updatedValues);
            setFilterOperators(updatedOperators);
        }
    };

    const handleFilterChange = (fieldName, value) => {
        // for dates we need the format "startDate:endData"
        if (["closingDate", "createdAt"].includes(fieldName)) {
            const { type, value: dateValue } = value; // Destructure here to avoid `type` not being defined
            setFilterValues((prevValues) => {
                const currentDates = prevValues[fieldName]?.split(';') || [null, null];
                const updatedDates = type === 'from'
                    ? [dateValue?.format('YYYY-MM-DD') || '', currentDates[1] || '']
                    : [currentDates[0] || '', dateValue?.format('YYYY-MM-DD') || ''];
                return {
                    ...prevValues,
                    [fieldName]: updatedDates.join(';'),
                };
            });
        }
        else {
            setFilterValues({ ...filterValues, [fieldName]: value });
        }
    };

    const handleOperatorChange = (fieldName, operator) => {
        setFilterOperators({ ...filterOperators, [fieldName]: operator });
    };

    const handleApplyFilter = () => {
        const getFieldType = (fieldName) => {
            // field type needed in backend
            const intFields = ["stage", "amount", "accountId", "contactId"];
            const dateFields = ["closingDate", "createdAt"];
            const relationalFields = ["account.accountName", "contact.firstName"];

            if (intFields.includes(fieldName)) return 0;
            if (dateFields.includes(fieldName)) return 2;
            if (relationalFields.includes(fieldName)) return 3;

            return 1; // 0 = int, 1 = string
        };

        const filterParams = checkedFields
            .filter((field) => field.checked)
            .map((field) => ({

                fieldName: field.fieldName,
                filterOperator: filterOperators[field.fieldName],
                fieldValue: filterValues[field.fieldName],
                fieldType: getFieldType(field.fieldName) // Assign correct field type dynamically
            }));

        setFilters(filterParams);
    };

    const handleClear = () => {
        setCheckedFields((prev) => prev.map((field) => ({ ...field, checked: false })));
        setFilterValues({});
        setFilterOperators({});
        setFilters([]);
        localStorage.removeItem('dealsFilters');
    };

    return (
        <Box p={2} border="1px solid #ccc" borderRadius="8px" sx={{
            height: '100%', // Ensures the component takes the full height of the parent
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box'
        }}>
            <Typography variant="subtitle1" gutterBottom>
                Filter By Fields
            </Typography>
            <Box display="flex" flexDirection="column" gap={1} height="100%" overflow="auto">
                {checkedFields.map(({ fieldName, label, checked }) => (
                    <Box key={fieldName}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={checked}
                                    onChange={() => handleCheckboxChange(fieldName)}
                                />
                            }
                            label={label}
                        />
                        {checked && ["dealName", "country", "account.accountName", "contact.firstName"].includes(fieldName) && (
                            <Box display="flex" flexDirection="column" mt={1} mr={1}>
                                <FormControl fullWidth margin="dense">
                                    <InputLabel>Operator</InputLabel>
                                    <Select
                                        label="Operator"
                                        value={filterOperators[fieldName] || 0}
                                        size="small"
                                        onChange={(e) =>
                                            handleOperatorChange(fieldName, e.target.value)
                                        }
                                    >
                                        <MenuItem value={0}>Equals</MenuItem>
                                        <MenuItem value={2}>Contains</MenuItem>
                                        <MenuItem value={4}>Starts With</MenuItem>
                                        <MenuItem value={5}>Ends With</MenuItem>
                                    </Select>
                                </FormControl>
                                <TextField
                                    fullWidth
                                    margin="dense"
                                    label="Type here"
                                    size="small"
                                    value={filterValues[fieldName] || ""}
                                    onChange={(e) =>
                                        handleFilterChange(fieldName, e.target.value)
                                    }
                                />
                            </Box>
                        )}
                        {checked && fieldName === "accountId" && (
                            <Box display="flex" flexDirection="column" gap={1} mt={1} mr={1}>
                                <AccountsAutoComplete
                                    accountId={filterValues[fieldName]}
                                    setAccountId={(value) => handleFilterChange(fieldName, value.toString())}
                                    shouldFetchSingleAccount={filterValues[fieldName] > 0}
                                    small={true}
                                />
                            </Box>
                        )}
                        {checked && fieldName === "contactId" && (
                            <Box display="flex" flexDirection="column" gap={1} mt={1} mr={1}>
                                <ContactsAutoComplete
                                    contactId={filterValues[fieldName]}
                                    setContactId={(value) => handleFilterChange(fieldName, value.toString())}
                                    shouldFetchSingleContact={filterValues[fieldName] > 0}
                                    small={true}
                                />
                            </Box>
                        )}
                        {checked && ["closingDate", "createdAt"].includes(fieldName) && (
                            <Box display="flex" flexDirection="column" gap={1} mt={1} mr={1}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label="From Date"
                                        value={fieldName == "createdAt" ? fromDate : fromDateClosingDate}
                                        fullWidth
                                        required
                                        onChange={(newDate) => handleFilterChange(fieldName, { type: 'from', value: newDate })}
                                        slotProps={{
                                            textField: {
                                                variant: 'outlined',
                                                fullWidth: true,
                                                size: 'small'
                                            },
                                        }}
                                    />
                                </LocalizationProvider>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label="To Date"
                                        value={fieldName == "createdAt" ? toDate : toDateClosingDate}
                                        fullWidth
                                        required
                                        onChange={(newDate) => handleFilterChange(fieldName, { type: 'to', value: newDate })}
                                        slotProps={{
                                            textField: {
                                                variant: 'outlined',
                                                fullWidth: true,
                                                size: 'small'
                                            },
                                        }}
                                    />
                                </LocalizationProvider>
                            </Box>
                        )

                        }
                        {checked && ["createdBy", "updatedBy", "ownerId"].includes(fieldName) && (
                            <Box display="flex" flexDirection="column" gap={1} mt={1} mr={1}>
                                <UsersAutoComplete
                                    userId={filterValues[fieldName]} // Pass the current filter value for the field
                                    setUserId={(value) => handleFilterChange(fieldName, value)} // Call handleFilterChange with the field and selected value
                                    small={true}
                                />
                            </Box>
                        )}
                        {checked && fieldName === "stage" && (
                            <Box display="flex" flexDirection="column" gap={1} mt={1} mr={1}>
                                <FormControl fullWidth margin="dense">
                                    <InputLabel>Operator</InputLabel>
                                    <Select
                                        label="Operator"
                                        value={filterOperators[fieldName] || 0}
                                        size="small"
                                        onChange={(e) =>
                                            handleOperatorChange(fieldName, e.target.value)
                                        }
                                    >
                                        <MenuItem value={0}>Is</MenuItem>
                                        <MenuItem value={1}>Is Not</MenuItem>
                                    </Select>
                                </FormControl>
                                <Autocomplete
                                    size="small"
                                    disablePortal
                                    value={DealStage.find((option) => option.value.toString() === filterValues[fieldName])}
                                    options={DealStage}
                                    sx={{ width: "100%" }}
                                    onChange={(event, newValue) => handleFilterChange(fieldName, newValue.value.toString())}
                                    renderInput={(params) => <TextField {...params} label="Deal Stage" />}
                                />
                            </Box>
                        )}
                        {checked && fieldName === "amount" && (
                            <Box display="flex" flexDirection="column" gap={1} mt={1} mr={1}>
                                <FormControl fullWidth margin="dense">
                                    <InputLabel>Operator</InputLabel>
                                    <Select
                                        label="Operator"
                                        placeholder="Choose Operator"
                                        value={filterOperators[fieldName] || 0}
                                        size="small"
                                        onChange={(e) =>
                                            handleOperatorChange(fieldName, e.target.value)
                                        }
                                    >
                                        <MenuItem value={0}>=</MenuItem>
                                        <MenuItem value={1}>!=</MenuItem>
                                    </Select>
                                </FormControl>
                                <TextField
                                    fullWidth
                                    margin="dense"
                                    label="Type here"
                                    size="small"
                                    value={filterValues[fieldName] || ""}
                                    onChange={(e) =>
                                        handleFilterChange(fieldName, e.target.value)
                                    }
                                />
                            </Box>
                        )}
                    </Box>
                ))}
            </Box>
            <Box mt={2} display="flex" justifyContent="space-between">
                <Button variant="contained" color="primary" onClick={handleApplyFilter}>
                    Apply Filter
                </Button>
                <Button variant="outlined" onClick={handleClear}>
                    Clear
                </Button>
            </Box>
        </Box>
    );
};

export default FilterComponent;
